import { useEffect, useRef, useState } from "react";
import { Bell, BellRing, Mail, Send, CalendarCheck, TrendingDown, TrendingUp } from "lucide-react";
import { useSlip } from "@/context/SlipContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, notificationsSupported, getPushSubscription, enablePush, disablePush, showTabNotification } from "@/lib/push";

const TAB_KEY = "parlaylab_tab_alerts";
const TYPE_LABEL = { parlay_settled: "Parlay settled", lock_settled: "Lock of the Day", daily_slate: "Daily slate", weekly_recap: "Sunday digest", streak_alert: "Streak alert", contest_reminder: "Pick 5", contest_won: "Contest", standings_move: "Standings move", power_move: "Power rankings", team_alert: "Your teams", prop_streak: "Prop streak", welcome: "Welcome", referral_joined: "Referral reward", trend_alert: "Trend alert", crew_ticket: "Crew ticket", referral_champion: "Referral champion" };

export function NotificationBell() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [push, setPush] = useState(false);
  const [tabAlerts, setTabAlerts] = useState(() => localStorage.getItem(TAB_KEY) === "1");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const seen = useRef(null);
  const unread = items.filter((n) => !n.read).length;
  const alerts = user?.alerts_enabled !== false;
  const digest = user?.digest_enabled !== false;
  const { toggle: slipToggle, has: slipHas } = useSlip();
  const [streakBusy, setStreakBusy] = useState("");
  const streakPick = async (e, n, mode) => {
    e.stopPropagation();
    const slug = (n.url || "").split("/players/")[1];
    if (!slug) return;
    setStreakBusy(n.notif_id + mode);
    try {
      const { data } = await api.post(`/players/${slug}/streak-parlay`, { mode, single: true });
      const leg = data.legs[0];
      if (!slipHas(leg.pick_id)) slipToggle(leg);
      toast.success(`${mode === "fade" ? "Fade" : "Ride"} added to your slip: ${leg.selection} ${leg.odds > 0 ? "+" : ""}${leg.odds}`);
      setOpen(false);
      navigate("/builder");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setStreakBusy("");
    }
  };
  const supported = pushSupported();

  useEffect(() => {
    getPushSubscription().then((s) => setPush(!!s)).catch(() => {});
  }, []);

  useEffect(() => {
    const poll = () =>
      api.get("/notifications").then((r) => {
        const list = r.data;
        if (seen.current) {
          const fresh = list.filter((n) => !seen.current.has(n.notif_id));
          if (fresh.length && !push && (tabAlerts || (notificationsSupported() && Notification.permission === "granted" && !supported)))
            fresh.slice(0, 3).forEach((n) => showTabNotification(n.title, n.body, n.url || "/dashboard"));
        }
        seen.current = new Set(list.map((n) => n.notif_id));
        setItems(list);
      }).catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [push, tabAlerts, supported]);

  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const toggleOpen = async () => {
    setOpen((o) => !o);
    if (!open && unread) {
      await api.post("/notifications/read-all").catch(() => {});
      setItems((l) => l.map((n) => ({ ...n, read: true })));
    }
  };
  const toggleAlerts = async () => {
    const { data } = await api.patch("/users/me/preferences", { alerts_enabled: !alerts });
    setUser(data);
  };
  const toggleDigest = async () => {
    const { data } = await api.patch("/users/me/preferences", { digest_enabled: !digest });
    setUser(data);
    toast.success(data.digest_enabled === false ? "Sunday digest off — you'll still get daily alerts if they're on" : "Sunday digest on — lands every Sunday afternoon");
  };
  const togglePush = async () => {
    setBusy(true);
    try {
      if (supported) {
        if (push) {
          await disablePush();
          setPush(false);
          toast.success("Push alerts turned off");
        } else {
          await enablePush();
          setPush(true);
          toast.success("Push alerts on — you'll be notified even with the tab closed");
        }
      } else if (notificationsSupported()) {
        if (tabAlerts) {
          localStorage.removeItem(TAB_KEY);
          setTabAlerts(false);
        } else {
          const p = await Notification.requestPermission();
          if (p !== "granted") throw new Error("Notification permission was denied.");
          localStorage.setItem(TAB_KEY, "1");
          setTabAlerts(true);
          toast.success("In-tab alerts on (this browser doesn't support background push)");
        }
      } else throw new Error("This browser does not support notifications.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const sendTest = async () => {
    try {
      const { data } = await api.post("/push/test");
      if (!push) showTabNotification("Push alerts are live", "You'll hear from the Lab the moment a parlay cashes or the Lock settles.");
      toast.success(data.sent ? `Test alert sent to ${data.sent} device${data.sent > 1 ? "s" : ""}` : "Test alert fired");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pushOn = supported ? push : tabAlerts;

  return (
    <div className="relative" ref={ref}>
      <button data-testid="nav-notifications-btn" onClick={toggleOpen} className="relative rounded-full p-2 text-slate-400 transition-colors hover:bg-[#182030] hover:text-white">
        {pushOn ? <BellRing size={17} className="text-emerald-400" /> : <Bell size={17} />}
        {unread > 0 && <span data-testid="nav-notifications-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 font-mono-num text-[10px] font-bold text-[#0B0E14]">{unread}</span>}
      </button>
      {open && (
        <div data-testid="notifications-dropdown" className="glass-card fade-up absolute right-0 top-11 z-50 w-[24rem] max-w-[calc(100vw-1rem)] overflow-hidden !bg-[#121824] shadow-2xl">
          <div className="border-b border-[#26334D] px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="font-display text-lg font-bold uppercase tracking-tight text-white">Alerts</span>
              <div className="flex items-center gap-1.5">
                <button data-testid="alerts-toggle" onClick={toggleAlerts} className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${alerts ? "border-emerald-500/50 text-emerald-300" : "border-[#26334D] text-slate-500"}`}>
                  <Mail size={11} /> Email {alerts ? "on" : "off"}
                </button>
                <button data-testid="digest-toggle" onClick={toggleDigest} title="Sunday digest email" className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${digest ? "border-emerald-500/50 text-emerald-300" : "border-[#26334D] text-slate-500"}`}>
                  <CalendarCheck size={11} /> Digest {digest ? "on" : "off"}
                </button>
                <button data-testid="push-toggle" disabled={busy} onClick={togglePush} className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${pushOn ? "border-emerald-500/50 text-emerald-300" : "border-[#26334D] text-slate-500"}`}>
                  <BellRing size={11} /> Push {pushOn ? "on" : "off"}
                </button>
              </div>
            </div>
            <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500" data-testid="push-help">
              {pushOn ? <>Browser alerts fire when a parlay cashes, the Lock of the Day settles, a player you follow or bet on hits 5 straight, the morning slate drops, a team you have a ticket on moves in the table and your Sunday digest lands. <button data-testid="push-test-btn" onClick={sendTest} className="inline-flex items-center gap-1 text-cyan-400 underline"><Send size={10} /> Send a test</button></> : supported ? "Turn on push to get a browser notification the moment a parlay cashes or the Lock settles — even with the tab closed." : "Background push isn't supported here; turn on in-tab alerts instead."}
            </p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && <p className="px-4 py-8 text-center text-xs text-slate-500" data-testid="notifications-empty">No alerts yet. You'll be notified when a fresh slate drops or a ticket settles.</p>}
            {items.map((n) => (
              <div key={n.notif_id} role="button" tabIndex={0} data-testid="notification-item" onClick={() => { setOpen(false); navigate(n.url || "/dashboard"); }} onKeyDown={(e) => e.key === "Enter" && navigate(n.url || "/dashboard")} className="block w-full cursor-pointer border-b border-[#26334D]/60 px-4 py-3 text-left transition-colors hover:bg-[#182030]">
                <p className="text-sm font-semibold text-white">{n.title}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{n.body}</p>
                {n.type === "prop_streak" && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="streak-actions">
                    <button data-testid="streak-fade-btn" disabled={!!streakBusy} onClick={(e) => streakPick(e, n, "fade")} className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/60 bg-sky-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-sky-200 transition-colors hover:bg-sky-500/25 disabled:opacity-60"><TrendingDown size={12} /> {streakBusy === n.notif_id + "fade" ? "Adding…" : "Fade the regression"}</button>
                    <button data-testid="streak-ride-btn" disabled={!!streakBusy} onClick={(e) => streakPick(e, n, "ride")} className="inline-flex items-center gap-1.5 rounded-full border border-orange-400/60 bg-orange-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-orange-200 transition-colors hover:bg-orange-500/25 disabled:opacity-60"><TrendingUp size={12} /> {streakBusy === n.notif_id + "ride" ? "Adding…" : "Ride it"}</button>
                  </div>
                )}
                <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-600">{new Date(n.created_at).toLocaleString()} · {TYPE_LABEL[n.type] || n.type}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
